import React from 'react';
import PropTypes from 'prop-types';
import utils from '../utils/utils';
import { Page, Text, View, Document, StyleSheet, Font } from '@react-pdf/renderer';
import DATE_FORMAT from '../components/constants/dateFormat';
import regularFont from '../assets/fonts/Metropolis-Regular.ttf';
import boldFont from '../assets/fonts/Metropolis-SemiBold.ttf';

const OrderBatchBillPdfDocument = ({ orders, marketplaces, orderBatch, copy }) => {
  const BORDER_COLOR = '#bfbfbf';
  const BORDER_STYLE = 'solid';

  const MEMBO_BILL_DETAILS = {
    companyName: 'Membo OÜ',
    registryCode: '16093334',
    vatNumber: 'EE102334739',
    address: 'Volberi, Kaunissaare küla Anija vald Harjumaa 74309',
  };

  Font.register({
    family: 'metropolis',
    fonts: [{ src: regularFont }, { src: boldFont, fontWeight: 600 }],
  });

  const styles = StyleSheet.create({
    body: {
      padding: 30,
      fontSize: 12,
      fontFamily: 'metropolis',
    },
    title: {
      fontSize: 24,
      marginTop: 50,
      marginBottom: 50,
    },
    h3: {
      fontSize: 16,
    },
    small: {
      fontSize: 10,
    },
    bold: {
      fontWeight: 700,
    },
    info: {
      marginBottom: 50,
    },
    table: {
      display: 'table',
      width: 'auto',
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderRightWidth: 0,
      borderBottomWidth: 0,
    },
    tableRow: {
      margin: 'auto',
      flexDirection: 'row',
    },
    tableColHeader: {
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0,
    },
    tableCol: {
      borderStyle: BORDER_STYLE,
      borderColor: BORDER_COLOR,
      borderWidth: 1,
      borderLeftWidth: 0,
      borderTopWidth: 0,
    },
    tableCol1: {
      width: '60%',
    },
    tableCol2: {
      width: '15%',
    },
    tableCol3: {
      width: '25%',
    },
    tableCellHeader: {
      margin: 5,
      fontWeight: 700,
    },
    tableCell: {
      margin: 5,
    },
    footer: {
      position: 'absolute',
      bottom: 30,
      left: 30,
    },
  });

  const orderPages = orders.map((order) => {
    const marketplace = marketplaces.find((mp) => mp.id === order.marketplaceId);

    if (!marketplace) {
      return <View></View>;
    }

    const billFrom = marketplace.endConsumerBillsEnabled
      ? {
          companyName: marketplace.billDetails.companyName,
          registryCode: marketplace.billDetails.registryCode,
          vatNumber: marketplace.billDetails.vatNumber,
          address: marketplace.billDetails.address.display,
        }
      : MEMBO_BILL_DETAILS;

    const shouldShowVatInfo = !!order.finalSumWithoutVat;
    const title = `${copy.order.title} #${order.id}`;
    const dateCreated = utils.getDateFromString(order.dateCreated).format(DATE_FORMAT.displayWithTime);

    const itemRows = order.items.map((orderItem, index) =>
      getBillItemRow(styles, copy, index, orderItem.itemName, orderItem.quantity, orderItem.itemPrice)
    );

    return getBillPage(
      styles,
      copy,
      title,
      billFrom,
      shouldShowVatInfo,
      dateCreated,
      itemRows,
      order.finalSumWithoutVat,
      order.finalSum
    );
  });

  const deliveryBillRows = [
    getBillItemRow(styles, copy, 'delivery', `${copy.table.delivery}`, 1, orderBatch.deliveryFee),
  ];

  const deliveryFeeWithoutVat = utils.getRoundedPrice(orderBatch.deliveryFee / 1.2);

  const orderBatchBill = getBillPage(
    styles,
    copy,
    `Tarne #${orderBatch.id}`,
    MEMBO_BILL_DETAILS,
    true,
    utils.getDateFromString(orderBatch.dateCreated).format(DATE_FORMAT.displayWithTime),
    deliveryBillRows,
    deliveryFeeWithoutVat,
    orderBatch.deliveryFee
  );

  const totalBillRows = [
    ...orders.map((order, index) => {
      return getBillItemRow(styles, copy, index, `${copy.combined.order} #${order.id}`, 1, order.finalSum);
    }),
    deliveryBillRows,
  ];

  const refundsSum = orderBatch.payment.refunds.reduce((sum, refund) => sum + refund.amount, 0);
  totalBillRows.push(
    orderBatch.payment.refunds.map((refund, index) =>
      getBillItemRow(styles, copy, index, `${copy.combined.refund} #${refund.id}`, 1, -refund.amount)
    )
  );

  if (orderBatch.coupon) {
    if (orderBatch.coupon.discountSum != null) {
      totalBillRows.push(
        getBillItemRow(
          styles,
          copy,
          'coupon',
          `${copy.combined.coupon} ${orderBatch.coupon.code}`,
          1,
          -orderBatch.coupon.discountSum
        )
      );
    } else {
      totalBillRows.push(
        getBillItemRow(
          styles,
          copy,
          'coupon',
          `${copy.combined.coupon} ${orderBatch.coupon.code}`,
          1,
          -utils.getRoundedPrice(orderBatch.totalSum + orderBatch.deliveryFee - orderBatch.finalSum)
        )
      );
    }
  }

  const finalSum = orderBatch.finalSum - refundsSum;
  const finalSumWithoutVat = utils.getRoundedPrice(finalSum / 1.2);

  const totalPage = getBillPage(
    styles,
    copy,
    copy.combined.header,
    null,
    false,
    utils.getDateFromString(orderBatch.dateCreated).format(DATE_FORMAT.displayWithTime),
    totalBillRows,
    finalSumWithoutVat,
    finalSum
  );

  return (
    <Document>
      {orderPages}
      {orderBatchBill}
      {totalPage}
    </Document>
  );
};

function getBillItemRow(styles, copy, index, name, quantity, price) {
  return (
    <View key={index} style={styles.tableRow}>
      <View style={[styles.tableCol, styles.tableCol1]}>
        <Text style={styles.tableCell}>{name}</Text>
      </View>
      <View style={[styles.tableCol, styles.tableCol2]}>
        <Text style={styles.tableCell}>{quantity}</Text>
      </View>
      <View style={[styles.tableCol, styles.tableCol3]}>
        <Text style={styles.tableCell}>{price}€</Text>
      </View>
    </View>
  );
}

function getBillPage(
  styles,
  copy,
  title,
  billDetails,
  shouldShowVatInfo,
  dateCreated,
  itemRows,
  finalSumWithoutVat,
  finalSum
) {
  return (
    <Page style={styles.body}>
      <Text style={styles.title}>{title}</Text>

      {billDetails ? (
        <View style={styles.info}>
          <Text>
            <Text style={styles.bold}>{copy.from.companyName}: </Text>
            <Text>{billDetails.companyName}</Text>
          </Text>
          <Text>
            <Text style={styles.bold}>{copy.from.buyer}: </Text>
            <Text>{copy.from.buyerValue}</Text>
          </Text>
          <Text>
            <Text style={styles.bold}>{copy.from.registryCode}: </Text>
            <Text>{billDetails.registryCode}</Text>
          </Text>
          <Text>
            <Text style={styles.bold}>{copy.from.vatNumber}: </Text>
            <Text>{shouldShowVatInfo ? billDetails.vatNumber : '-'}</Text>
          </Text>
          <Text>
            <Text style={styles.bold}>{copy.from.address}: </Text>
            <Text>{billDetails.address}</Text>
          </Text>
          <Text>
            <Text style={styles.bold}>{copy.from.date}: </Text>
            <Text>{dateCreated}</Text>
          </Text>
        </View>
      ) : (
        <View></View>
      )}

      <View style={[styles.table, { marginBottom: 10 }]}>
        <View style={styles.tableRow}>
          <View style={[styles.tableColHeader, styles.tableCol1]}>
            <Text style={styles.tableCellHeader}>{copy.table.product}</Text>
          </View>
          <View style={[styles.tableColHeader, styles.tableCol2]}>
            <Text style={styles.tableCellHeader}>{copy.table.quantity}</Text>
          </View>
          <View style={[styles.tableColHeader, styles.tableCol3]}>
            <Text style={styles.tableCellHeader}>{shouldShowVatInfo ? copy.table.totalWithVat : copy.table.total}</Text>
          </View>
        </View>

        {itemRows}
      </View>

      {shouldShowVatInfo ? (
        <Text style={[{ textAlign: 'right', marginBottom: 5 }]}>
          <Text>{copy.total.totalWithoutVat}: </Text>
          <Text style={[styles.bold]}>{finalSumWithoutVat}€</Text>
        </Text>
      ) : (
        <View></View>
      )}
      <Text style={[styles.h3, { textAlign: 'right' }]}>
        <Text>{shouldShowVatInfo ? copy.total.totalWithVat : copy.total.total}: </Text>
        <Text style={[styles.bold]}>{finalSum}€</Text>
      </Text>
    </Page>
  );
}

OrderBatchBillPdfDocument.propTypes = {
  orders: PropTypes.array.isRequired,
  marketplaces: PropTypes.array.isRequired,
  orderBatch: PropTypes.object.isRequired,
  copy: PropTypes.object.isRequired,
};

OrderBatchBillPdfDocument.defaultProps = {};

export default OrderBatchBillPdfDocument;
